import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import { z } from 'zod'
import {
  loadImage,
  removeImageFromCache,
} from 'mixtiles-web-common/services/imageLoader'
import { BasePhotoObject } from 'components/PhotoBank/PhotoBank.schemas'
import { logger } from 'services/logger'
import { extractErrorMessage } from 'utils/ApiUtils'
import {
  type LocalPhoto,
  type Photo,
  type RawRemotePhoto,
  type RemotePhoto,
} from 'components/PhotoBank/PhotoBank.types'
import {
  getPhotoPreviewUrl,
  isPrivatePhotoUrl,
  PERSISTED_PHOTO_BANK_FIELDS,
} from 'components/PhotoBank/PhotoBank.utils'
import { PHOTO_PREVIEW_SIZES } from 'components/PhotoBank/PhotoBank.consts'

type PhotoFieldSchemas = typeof BasePhotoObject.shape
type DefaultedPhotoField = {
  [Field in keyof PhotoFieldSchemas]: PhotoFieldSchemas[Field] extends z.ZodDefault<z.ZodTypeAny>
    ? Field
    : never
}[keyof PhotoFieldSchemas]

const photoFieldSchemas: z.ZodRawShape = BasePhotoObject.shape

function isDefaultedPhotoField(field: string): field is DefaultedPhotoField {
  return photoFieldSchemas[field] instanceof z.ZodDefault
}

// Parsing a stored photo fills these back in, so a field at its default is left out of storage and the sync.
const PHOTO_FIELD_DEFAULTS = Object.keys(BasePhotoObject.shape)
  .filter(isDefaultedPhotoField)
  .map((field) => ({
    field,
    defaultValue: BasePhotoObject.shape[field].parse(undefined),
  }))

const PRIVATE_PHOTO_DERIVED_URL_FIELDS = [
  'minimalResUrl',
  'lowResUrl',
  'highResUrl',
] as const

export function cleanupLocalPhotoResources(photo: LocalPhoto): void {
  URL.revokeObjectURL(photo.localUrl)
  URL.revokeObjectURL(photo.minimalResUrl)
  URL.revokeObjectURL(photo.lowResUrl)
  URL.revokeObjectURL(photo.highResUrl)
  removeImageFromCache(photo.localUrl)
}

// Swapping a local photo to URLs the browser hasn't loaded yet blanks it until they download. A preview that
// fails to load here is still shown: the image element retries it.
export async function preloadPhotoPreviews(originalUrl: string): Promise<void> {
  try {
    await Promise.all([
      loadImage(getPhotoPreviewUrl(originalUrl, PHOTO_PREVIEW_SIZES.MIN)),
      loadImage(getPhotoPreviewUrl(originalUrl, PHOTO_PREVIEW_SIZES.HIGH)),
    ])
  } catch (e) {
    logger.warning('Uploaded photo preview failed to preload', {
      message: extractErrorMessage(e),
    })
  }
}

// Display URLs of private photos are derived from the key when a photo is parsed, so they aren't persisted (locally
// or to the account)
export function getPersistedRemotePhotos(
  photos: RemotePhoto[]
): RawRemotePhoto[] {
  return photos.map((photo) => {
    const persistedPhoto = pick(photo, PERSISTED_PHOTO_BANK_FIELDS)
    const fieldsAtDefault = PHOTO_FIELD_DEFAULTS.flatMap(
      ({ field, defaultValue }) =>
        isEqual(persistedPhoto[field], defaultValue) ? [field] : []
    )
    return omit(persistedPhoto, [
      ...fieldsAtDefault,
      ...(isPrivatePhotoUrl(photo.originalUrl)
        ? PRIVATE_PHOTO_DERIVED_URL_FIELDS
        : []),
    ])
  })
}

export function buildPhotoHashState(
  localPhotos: LocalPhoto[],
  remotePhotos: RemotePhoto[]
): {
  knownHashes: Set<string>
  localHashToPhoto: Map<string, LocalPhoto>
  remoteHashToPhoto: Map<string, RemotePhoto>
} {
  const localHashToPhoto = new Map(
    localPhotos
      .filter((photo): photo is LocalPhoto & { hashCode: string } =>
        Boolean(photo.hashCode)
      )
      .map((photo) => [photo.hashCode, photo] as const)
  )
  const remoteHashToPhoto = new Map(
    remotePhotos
      .filter((photo): photo is RemotePhoto & { hashCode: string } =>
        Boolean(photo.hashCode)
      )
      .map((photo) => [photo.hashCode, photo] as const)
  )

  return {
    localHashToPhoto,
    remoteHashToPhoto,
    knownHashes: new Set([
      ...localHashToPhoto.keys(),
      ...remoteHashToPhoto.keys(),
    ]),
  }
}

export function orderPhotosByUid(
  orderedPhotoUids: string[],
  photos: Iterable<Photo>
): Photo[] {
  const photosByUid = new Map(
    Array.from(photos, (photo) => [photo.uid, photo] as const)
  )

  return orderedPhotoUids
    .map((uid) => photosByUid.get(uid))
    .filter((photo): photo is Photo => Boolean(photo))
}
